import BottomBar from './BottomBar';

import { useEffect, useMemo, useState } from 'react';
import { calculateTotals, getFormActionButtons, getFormTitle, transformFormData } from '../../utils';
import { handleUpdateRowFields } from '../../utils/calc-util';
import { formSchema, initialFormValues } from '../../schema';
import { AritoFormV2 } from '@/components/custom/erp';
import { useAuth } from '@/contexts/auth-context';
import { useFormFieldState } from '../../hooks';
import { BasicInfoTab } from './BasicInfoTab';
import InfoDropdown from './InfoDropdown';
import { HistoryTab } from './HistoryTab';
import { FormMode } from '@/types/form';

import { DetailTab } from './DetailTab';
import { useDetailRows } from './hooks';
import { TyGiaTab } from './TyGiaTab';
import { OtherTab } from './OtherTab';

interface FormDialogProps {
  formMode: FormMode;
  open?: boolean;
  initialData?: MuaHangRecord;
  onSubmit?: (data: MuaHangRecord) => void;
  onClose: () => void;
  onAdd?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onCopy?: () => void;
}

const FormDialog = ({
  open,
  formMode,
  initialData,
  onClose,
  onSubmit,
  onAdd,
  onEdit,
  onDelete,
  onCopy
}: FormDialogProps) => {
  const [activeTab, setActiveTab] = useState<string>('info');
  const { entityUnit } = useAuth();
  const {
    rows: detailRows,
    selectedRowUuid: detailSelectedRowUuid,
    selectedCell: detailSelectedCell,
    handleRowClick: detailHandleRowClick,
    handleCellClick: detailHandleCellClick,
    handleAddRow: detailHandleAddRow,
    handleDeleteRow: detailHandleDeleteRow,
    handleCopyRow: detailHandleCopyRow,
    handlePasteRow: detailHandlePasteRow,
    handleMoveRow: detailHandleMoveRow,
    handleCellValueChange: detailHandleCellValueChange
  } = useDetailRows(initialData?.chi_tiet_data || [], undefined, handleUpdateRowFields);

  const { state, actions } = useFormFieldState(initialData);

  useEffect(() => {
    if (!open) {
      setActiveTab('info');
    }
  }, [open]);

  const { tongTien, tongThanhToan } = useMemo(() => {
    return calculateTotals(detailRows);
  }, [detailRows]);

  const handleSubmit = (data: MuaHangRecord) => {
    const formData = transformFormData(data, state, formMode, tongTien, tongThanhToan, detailRows);
    onSubmit?.({ ...formData, unit_id: entityUnit?.uuid });
  };

  const handleClose = () => {
    onClose();
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const title = getFormTitle(formMode, activeTab);
  const actionButtons = getFormActionButtons(formMode, activeTab, {
    onAdd,
    onEdit,
    onDelete,
    onCopy,
    handleClose
  });

  if (initialData && formMode === 'add') {
    initialData = {
      ...initialData,
      ngay_ct: new Date().toISOString().split('T')[0],
      ngay_lct: new Date().toISOString().split('T')[0]
    };
  }

  return (
    <>
      <AritoFormV2
        mode={formMode}
        initialData={initialData || initialFormValues}
        title={title}
        actionButtons={actionButtons}
        subTitle='Bút toán điều chỉnh giảm công nợ'
        onSubmit={handleSubmit}
        onClose={handleClose}
        schema={formSchema}
        headerFields={[
          {
            id: 'info',
            label: 'Thông tin',
            component: (
              <div>
                <BasicInfoTab formMode={formMode} formState={{ state, actions }} />
              </div>
            )
          },
          ...(formMode === 'view'
            ? [
                {
                  id: 'history',
                  label: 'Lịch sử',
                  component: <HistoryTab />
                }
              ]
            : [])
        ]}
        headerTabActions={formMode !== 'add' && <InfoDropdown uuid={initialData?.uuid} ma_ct={initialData?.chung_tu} />}
        tabs={
          activeTab === 'info' && [
            {
              id: 'details',
              label: 'Chi tiết',
              component: (
                <DetailTab
                  formMode={formMode}
                  rows={detailRows}
                  selectedRowUuid={detailSelectedRowUuid}
                  selectedCell={detailSelectedCell}
                  onRowClick={detailHandleRowClick}
                  onCellClick={detailHandleCellClick}
                  onAddRow={detailHandleAddRow}
                  onDeleteRow={detailHandleDeleteRow}
                  onCopyRow={detailHandleCopyRow}
                  onPasteRow={detailHandlePasteRow}
                  onMoveRow={detailHandleMoveRow}
                  onCellValueChange={detailHandleCellValueChange}
                  loaiChungTu={state.loaiChungTu}
                />
              )
            },
            {
              id: 'ty_gia',
              label: 'Tỷ giá',
              component: <TyGiaTab formMode={formMode} />
            },
            {
              id: 'other',
              label: 'Khác',
              component: <OtherTab formMode={formMode} />
            }
          ]
        }
        bottomBar={
          activeTab === 'info' && <BottomBar tongTien={tongTien} tongThanhToan={tongThanhToan} ty_gia={state.tyGia} />
        }
      />
    </>
  );
};

export default FormDialog;
